import {Injectable} from '@angular/core';
import {
	ActivatedRoute,
	ActivationEnd,
	Router
} from "@angular/router";
import {filter} from "rxjs";
import {environment} from "@env/environment";

@Injectable({
	providedIn: 'root'
})
export class FaviconService {

	private static readonly FAV_ICON_SIZE = 64;

	// Every institution keeps a favicon here, so this is always available as a fallback
	private readonly institutionFavIcon = `https://s3.amazonaws.com/creditsnapimages/${environment.institutionCode}/fav.png`;
	private merchantId: string | undefined;

	constructor(private activatedRoute: ActivatedRoute,
		private router: Router) {
	}

	/**
	 * Shows the merchant/agent bank mark in the browser tab on every page once a
	 * merchantId is known, falling back to the parent institution favicon.
	 */
	init() {
		// Read merchantId from the URL rather than waiting for the router, so the favicon
		// image loads start while the page is still loading. The tab spinner then covers
		// them and the institution favicon never flashes up first
		const merchantId = this.merchantIdFromUrl();
		if (merchantId) {
			this.setMerchantFavIcon(merchantId);
		} else {
			// No merchant to wait for, so the institution favicon can go in straight away
			this.applyFavIcon(this.institutionFavIcon);
		}

		// Favicon lives outside the router outlet so we subscribe to router events to get merchantId
		this.router.events.pipe(
			filter(event => event instanceof ActivationEnd)
		).subscribe((event) => {
			const activationEndEvent = event as ActivationEnd;
			if (activationEndEvent.snapshot.params) {
				this.setMerchantFavIcon(activationEndEvent.snapshot.params['merchantId']);
			}
		});

		this.activatedRoute.queryParams.subscribe(params => {
			this.setMerchantFavIcon(params['merchantId']);
		});
	}

	/**
	 * Reads merchantId the same way pages-routing.module.ts does: from the first path
	 * segment, or else from the merchantId query param.
	 */
	private merchantIdFromUrl(): string | null {
		const segments = location.pathname.split('/').filter(segment => segment.length > 0);
		if ((segments.length === 1 && segments[0] !== 'error') || segments.length === 2) {
			return decodeURIComponent(segments[0]);
		}
		return new URLSearchParams(location.search).get('merchantId');
	}

	/**
	 * Prefers the merchant favicon, then the merchant logo, then the institution favicon.
	 * A merchant logo whose mark can be told apart from the bank name is cut down to that
	 * mark, and shown whole when it cannot.
	 */
	private async setMerchantFavIcon(merchantId: string) {
		if (!merchantId || merchantId.length === 0 || merchantId === this.merchantId) {
			return;
		}
		this.merchantId = merchantId;

		const merchantImages = `${environment.s3Url}images/${environment.institutionCode}/${merchantId}`;
		const merchantFavIcon = `${merchantImages}_fav.png`;
		const merchantLogo = `${merchantImages}_logo.png`;

		// A favicon made for the merchant is already tab shaped, so it is used as it comes
		const favIcon = await this.loadImage(merchantFavIcon);
		if (this.isStale(merchantId)) {
			return;
		}
		if (favIcon) {
			this.applyFavIcon(merchantFavIcon);
			return;
		}

		// Otherwise the merchant's own logo. A merchant with no logo falls back to the
		// institution favicon rather than the institution logo, which is a wordmark and
		// would be unreadable at tab size
		const icon = await this.iconFromLogo(merchantLogo, merchantId);
		if (this.isStale(merchantId)) {
			return;
		}
		this.applyFavIcon(icon ?? this.institutionFavIcon);
	}

	/**
	 * The logo cut down to its mark, so the tab shows an icon rather than a shrunken bank
	 * name. Logos that set their mark off from the name by a clear gap can be cut this way;
	 * ones that run the two together are shown whole instead. Null means there is no logo.
	 */
	private async iconFromLogo(logoUrl: string, merchantId: string): Promise<string | null> {
		const logo = await this.loadReadableImage(logoUrl);
		if (!logo) {
			// A logo we cannot read the pixels of can still be shown whole, so tell a
			// missing logo apart from an unreadable one before giving up on it
			return await this.loadImage(logoUrl) ? logoUrl : null;
		}
		// Skip the pixel work entirely once the result can no longer be used
		const mark = this.isStale(merchantId) ? null : this.extractMark(logo);
		return mark ?? logoUrl;
	}

	/**
	 * A newer merchantId arrived while we were probing, so the result in hand is stale.
	 */
	private isStale(merchantId: string): boolean {
		return this.merchantId !== merchantId;
	}

	/**
	 * Preloads an image to check it exists, since a <link> has no usable error event.
	 * Unlike fetch, image loads keep the page loading, so the tab spinner stays on
	 * until the favicon is set.
	 */
	private loadImage(src: string, crossOrigin = false): Promise<HTMLImageElement | null> {
		return new Promise(resolve => {
			const image = new Image();
			if (crossOrigin) {
				image.crossOrigin = 'anonymous';
			}
			image.onload = () => resolve(image);
			image.onerror = () => resolve(null);
			image.src = src;
		});
	}

	/**
	 * Loads an image whose pixels we are allowed to read. The header shows the same logo
	 * through a plain <img>, and that cached response carries no CORS headers, so a
	 * crossOrigin load of the same URL would reuse it and fail. The query string gives
	 * this load a cache entry of its own.
	 */
	private loadReadableImage(src: string): Promise<HTMLImageElement | null> {
		return this.loadImage(`${src}?favicon`, true);
	}

	/**
	 * Merchant logos are wordmarks: a mark followed by the bank name. Shrunk whole into a
	 * tab they become an unreadable strip, so we lift out just the leading mark and centre
	 * it in a square. Returns a data URL, or null when the logo has no separable mark.
	 */
	private extractMark(logo: HTMLImageElement): string | null {
		const width = logo.naturalWidth;
		const height = logo.naturalHeight;
		if (!width || !height) {
			return null;
		}

		const source = document.createElement('canvas');
		source.width = width;
		source.height = height;
		const sourceContext = source.getContext('2d');
		if (!sourceContext) {
			return null;
		}
		sourceContext.drawImage(logo, 0, 0);

		let pixels: Uint8ClampedArray;
		try {
			pixels = sourceContext.getImageData(0, 0, width, height).data;
		} catch {
			// Canvas tainted, so the mark cannot be read out
			return null;
		}

		const isInk = this.inkTest(pixels, width, height);

		const columnInk = new Uint32Array(width);
		let inkBottom = -1;
		let inkTop = height;
		for (let y = 0; y < height; y++) {
			for (let x = 0; x < width; x++) {
				if (isInk((y * width + x) * 4)) {
					columnInk[x]++;
					if (y < inkTop) inkTop = y;
					if (y > inkBottom) inkBottom = y;
				}
			}
		}
		if (inkBottom < 0) {
			return null;
		}

		let left = 0;
		while (left < width && columnInk[left] === 0) left++;
		let right = width - 1;
		while (right >= 0 && columnInk[right] === 0) right--;
		const contentWidth = right - left + 1;

		// The mark is the first block of ink set off from the name by a clear gap;
		// anything narrower than this is just the spacing between letters
		const minimumGap = Math.max(2, Math.round((inkBottom - inkTop + 1) * 0.12));
		let markRight = -1;
		let x = left;
		while (x <= right) {
			if (columnInk[x] === 0) {
				let gapEnd = x;
				while (gapEnd <= right && columnInk[gapEnd] === 0) gapEnd++;
				if (gapEnd - x >= minimumGap) {
					markRight = x - 1;
					break;
				}
				x = gapEnd;
			} else {
				x++;
			}
		}
		if (markRight < 0) {
			return null;
		}

		const markWidth = markRight - left + 1;
		// Taking up most of the logo means the leading block is the name itself, not a mark
		if (markWidth > contentWidth * 0.6) {
			return null;
		}

		let markTop = height;
		let markBottom = -1;
		for (let y = 0; y < height; y++) {
			for (let cx = left; cx <= markRight; cx++) {
				if (isInk((y * width + cx) * 4)) {
					if (y < markTop) markTop = y;
					if (y > markBottom) markBottom = y;
					break;
				}
			}
		}
		if (markBottom < 0) {
			return null;
		}
		const markHeight = markBottom - markTop + 1;

		const size = FaviconService.FAV_ICON_SIZE;
		const target = document.createElement('canvas');
		target.width = size;
		target.height = size;
		const targetContext = target.getContext('2d');
		if (!targetContext) {
			return null;
		}

		// Fit the mark into the square tab slot without distorting it
		const scale = Math.min(size / markWidth, size / markHeight);
		const drawWidth = markWidth * scale;
		const drawHeight = markHeight * scale;
		targetContext.drawImage(
			logo,
			left, markTop, markWidth, markHeight,
			(size - drawWidth) / 2, (size - drawHeight) / 2, drawWidth, drawHeight
		);

		try {
			return target.toDataURL('image/png');
		} catch {
			return null;
		}
	}

	/**
	 * A transparent logo says what is ink by its alpha alone. Only a fully opaque one
	 * needs the colour of its backing plate worked out, and then from the most common
	 * edge colour rather than a corner the mark may happen to touch.
	 */
	private inkTest(pixels: Uint8ClampedArray, width: number, height: number): (index: number) => boolean {
		for (let i = 3; i < pixels.length; i += 4) {
			if (pixels[i] < 16) {
				return (index: number) => pixels[index + 3] > 16;
			}
		}

		const tally = new Map<number, number>();
		const note = (x: number, y: number) => {
			const i = (y * width + x) * 4;
			const key = (pixels[i] >> 3 << 10) | (pixels[i + 1] >> 3 << 5) | (pixels[i + 2] >> 3);
			tally.set(key, (tally.get(key) ?? 0) + 1);
		};
		for (let x = 0; x < width; x++) {
			note(x, 0);
			note(x, height - 1);
		}
		for (let y = 0; y < height; y++) {
			note(0, y);
			note(width - 1, y);
		}

		let commonest = -1;
		let backgroundKey = 0;
		tally.forEach((count, key) => {
			if (count > commonest) {
				commonest = count;
				backgroundKey = key;
			}
		});
		const bgRed = (backgroundKey >> 10 & 31) * 8;
		const bgGreen = (backgroundKey >> 5 & 31) * 8;
		const bgBlue = (backgroundKey & 31) * 8;

		return (index: number) => {
			if (pixels[index + 3] <= 16) {
				return false;
			}
			const dr = pixels[index] - bgRed;
			const dg = pixels[index + 1] - bgGreen;
			const db = pixels[index + 2] - bgBlue;
			return (dr * dr + dg * dg + db * db) > 48 * 48;
		};
	}

	private applyFavIcon(href: string) {
		const currentFavIcon: HTMLLinkElement | null = document.querySelector('#appFavIcon');
		if (currentFavIcon && currentFavIcon.href === href) {
			return;
		}

		// Replacing the element rather than updating href, as not every browser
		// re-reads the icon on an href change
		currentFavIcon?.remove();
		const favIcon = document.createElement('link');
		favIcon.id = 'appFavIcon';
		favIcon.rel = 'icon';
		favIcon.type = 'image/png';
		favIcon.href = href;
		document.head.appendChild(favIcon);
	}
}
